/**
 * Resolves the base URL of the camera microservice used by the OEM tray flow.
 *
 * The OEM tray will run its own camera-service instance alongside the tray-assembly
 * one (same host, different port). Until that instance is stood up, the OEM flow
 * should transparently share the tray-assembly camera. So this resolves
 * `VITE_OEM_CAMERA_API_URL` when it is set, and otherwise FALLS BACK to the
 * tray-assembly camera URL ({@link getCameraApiUrl}).
 *
 * Priority: runtime config (`window.__RUNTIME_CONFIG__`) → build-time env →
 * tray-assembly camera URL. Set `VITE_OEM_CAMERA_API_URL` to point the OEM flow at a
 * different host/port without touching the tray-assembly camera.
 */

import { getCameraApiUrl } from './camera-api-url';

interface RuntimeConfig {
  VITE_OEM_CAMERA_API_URL?: string;
}

interface WindowWithRuntimeConfig {
  __RUNTIME_CONFIG__?: RuntimeConfig;
}

interface GlobalWithImportMetaEnv {
  import?: {
    meta?: {
      env?: RuntimeConfig;
    };
  };
}

/**
 * Resolves the OEM camera API base URL. Falls back to the tray-assembly camera URL
 * ({@link getCameraApiUrl}) when `VITE_OEM_CAMERA_API_URL` is unset/blank, so today the
 * OEM flow shares the tray-assembly camera until its own instance is configured.
 */
export const getOemCameraApiUrl = (): string => {
  if (typeof window !== 'undefined') {
    const runtimeValue = (window as WindowWithRuntimeConfig).__RUNTIME_CONFIG__
      ?.VITE_OEM_CAMERA_API_URL;
    // Make sure it's not still a template placeholder AND not empty.
    if (
      runtimeValue &&
      runtimeValue !== '${VITE_OEM_CAMERA_API_URL}' &&
      runtimeValue.trim() !== ''
    ) {
      return runtimeValue;
    }
  }

  // Read build-time env without using import.meta syntax so Jest can compile it.
  const viteValue = (globalThis as GlobalWithImportMetaEnv).import?.meta?.env
    ?.VITE_OEM_CAMERA_API_URL;

  if (typeof viteValue === 'string' && viteValue.trim() !== '') {
    return viteValue;
  }

  if (typeof process !== 'undefined') {
    const processValue = process.env?.['VITE_OEM_CAMERA_API_URL'];

    if (typeof processValue === 'string' && processValue.trim() !== '') {
      return processValue;
    }
  }

  // No OEM-specific URL configured yet — share the tray-assembly camera.
  return getCameraApiUrl();
};
